// ═══════════════════════════════════════════════════════════════════════════
// VerComoButton — launcher de "Ver como" (staff → portal del cliente)
// ═══════════════════════════════════════════════════════════════════════════
//
// Botón en el sidebar (arriba de "Mi cuenta"), visible SOLO para staff Milbrands
// (Wizard / tipo 'milbrands'). Abre un picker: elegís empresa → te lista sus
// usuarios de portal (Owner, Operador, etc.) → click abre el PORTAL viéndolo
// como ese usuario en una PESTAÑA NUEVA (`/portal?impersonate=<uid>`).
//
// Por qué impersonación de usuario real y no `?verComo=<rol>`:
//   El guard `ClienteRoute` (App.jsx) usa `isRealCliente()`, que a propósito
//   IGNORA el rol simulado (rolSimulado) — así el admin que simula un rol no
//   es pateado al portal. `isRealCliente()` SÍ considera al usuario impersonado.
//   Entonces el único camino que efectivamente RENDERIZA el portal es
//   `?impersonate=<uid>` con un usuario real de esa empresa. Ese mecanismo ya
//   está probado (era el botón "Ver como cliente" de un `InvitarPortalButton`
//   que salió del repo el 29-ago-2026 por código muerto — report Jyoalp7h).
//
// IMPORTANTE — alcance de esto:
//   Es una simulación a NIVEL UI. El token de Firebase sigue siendo el del
//   admin (Wizard, que saltea rules y callables). Sirve para diseñar/validar
//   QUÉ VE y qué módulos/botones aparecen para Owner/Operador. NO reproduce la
//   capa de seguridad real que choca un cliente (rules + callables lo ven como
//   admin). Para QA fiel / "login as" de verdad haría falta un callable
//   server-side con createCustomToken (paso 2, no incluido acá).
// ═══════════════════════════════════════════════════════════════════════════

import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { collection, query, where, getDocs } from 'firebase/firestore'
import { useAuth } from '@core/contexts/AuthContext'
import { db } from '@core/lib/firebase'
import { docConId } from '@core/lib/docConId'
import { puedeImpersonar } from '@core/lib/permisos'
import { COLLECTIONS, USER_TIPOS } from '@core/lib/constants'
import SelectorEmpresaStaff from './SelectorEmpresaStaff'
import { createLogger } from '@core/lib/logger'

const log = createLogger('VerComo')

// Tipos de usuario que viven en el portal (no staff).
const TIPOS_PORTAL = [USER_TIPOS.CLIENTE, USER_TIPOS.COLABORADOR]

// Etiqueta legible para el rol del usuario (fallback al código o al tipo).
const ROL_LABEL = {
  OWNER_EMPRESA: 'Owner',
  OPERADOR: 'Operador',
  ASESOR: 'Asesor',
  CONTADOR: 'Contador',
  AUDITOR: 'Auditor',
  EMPLEADO: 'Empleado',
}
// Orden de aparición: primero Owner, después Operador, después el resto.
const ROL_ORDEN = { OWNER_EMPRESA: 0, OPERADOR: 1 }

function rolLabel(u) {
  if (u.rolId && ROL_LABEL[u.rolId]) return ROL_LABEL[u.rolId]
  if (u.rolId) return u.rolId
  return u.tipo === USER_TIPOS.COLABORADOR ? 'Colaborador' : 'Cliente'
}

function IconMask({ className = 'w-[18px] h-[18px]' }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  )
}

export default function VerComoButton({ collapsed = false }) {
  const { userDoc } = useAuth()
  const [open, setOpen] = useState(false)
  const [empresaId, setEmpresaId] = useState('')
  const [usuarios, setUsuarios] = useState([])
  const [cargando, setCargando] = useState(false)

  // Gate: sólo Wizard — la MISMA fuente que el efecto de impersonación en
  // AuthContext (`puedeImpersonar`, T7b). Antes mostraba el botón a todo staff
  // y el efecto lo ignoraba: un staff no-wizard abría su propio portal sin error.
  const esStaff = puedeImpersonar(userDoc)

  useEffect(() => {
    if (!open || !empresaId) { setUsuarios([]); return }
    let cancel = false
    setCargando(true)
    ;(async () => {
      try {
        const q = query(
          collection(db, COLLECTIONS.USUARIOS),
          where('empresaIds', 'array-contains', empresaId),
        )
        const snap = await getDocs(q)
        if (cancel) return
        const list = snap.docs
          .map(docConId)
          // Solo usuarios de portal (no staff), y activos.
          .filter(u => TIPOS_PORTAL.includes(u.tipo) && u.activo !== false)
          .sort((a, b) => {
            const oa = ROL_ORDEN[a.rolId] ?? 9
            const ob = ROL_ORDEN[b.rolId] ?? 9
            if (oa !== ob) return oa - ob
            return (a.nombre || '').localeCompare(b.nombre || '')
          })
        setUsuarios(list)
      } catch (e) {
        log.warn('cargar usuarios', e?.message)
        if (!cancel) setUsuarios([])
      } finally {
        if (!cancel) setCargando(false)
      }
    })()
    return () => { cancel = true }
  }, [open, empresaId])

  if (!esStaff) return null

  function abrirComo(uid) {
    // Pestaña nueva → tu sesión admin queda intacta en la pestaña original.
    window.open(`/portal?impersonate=${uid}`, '_blank', 'noopener,noreferrer')
  }

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        title={collapsed ? 'Ver como…' : 'Abrir el portal de un cliente en otra pestaña (solo staff)'}
        className={`flex items-center gap-2.5 w-full px-3 py-2.5 mt-2 rounded-xl text-sm font-medium transition-all duration-200 text-amber-300/90 hover:bg-amber-500/10 hover:text-amber-200 border border-amber-500/20 ${collapsed ? 'md:justify-center' : ''}`}
      >
        <IconMask />
        <span className={collapsed ? 'md:hidden' : ''}>Ver como…</span>
      </button>

      {/* Por portal a `document.body`: este botón vive adentro del `<aside>` de
          `Layout`, que lleva un `translate-x-…`. Un ancestro con transform es
          containing block de sus descendientes `fixed`, así que sin portal el
          overlay medía lo que mide la barra lateral y la caja quedaba adentro
          (medido el 4-oct-2026: 227 px de ancho; 35 px con la barra plegada). */}
      {open && createPortal((
        <div
          className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4"
          onClick={() => setOpen(false)}
        >
          <div
            className="bg-brand-bg border border-white/[0.08] rounded-2xl w-full max-w-md shadow-2xl max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-white/[0.06] flex items-center justify-between">
              <div className="min-w-0">
                <h3 className="font-semibold text-[14px] flex items-center gap-2">
                  <IconMask className="w-4 h-4 text-amber-300" />
                  Ver como cliente
                </h3>
                <p className="text-[12px] text-zinc-500 mt-0.5">
                  Abre el portal en otra pestaña. Tu sesión queda intacta acá.
                </p>
              </div>
              <button onClick={() => setOpen(false)} className="text-zinc-500 hover:text-white shrink-0">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wide text-zinc-500 mb-1.5">Empresa</label>
                <SelectorEmpresaStaff
                  value={empresaId}
                  onChange={setEmpresaId}
                  className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-zinc-200 focus:outline-none cursor-pointer"
                />
              </div>

              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wide text-zinc-500 mb-1.5">
                  Usuario del portal
                </label>

                {!empresaId ? (
                  <p className="text-[12px] text-zinc-600 py-2">Elegí una empresa para ver sus usuarios.</p>
                ) : cargando ? (
                  <p className="text-[12px] text-zinc-500 py-2">Cargando usuarios…</p>
                ) : usuarios.length === 0 ? (
                  <p className="text-[12px] text-zinc-500 py-2">
                    Esta empresa todavía no tiene usuarios de portal (Owner / Operador). Creá uno desde la invitación al portal para poder verla.
                  </p>
                ) : (
                  <div className="space-y-1.5 max-h-[280px] overflow-y-auto">
                    {usuarios.map(u => (
                      <button
                        key={u.id}
                        onClick={() => abrirComo(u.id)}
                        className="w-full flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-white/[0.03] hover:bg-amber-500/10 border border-white/[0.06] hover:border-amber-500/30 text-left transition-colors group"
                      >
                        <div className="min-w-0">
                          <div className="text-[13px] font-medium text-zinc-200 truncate">{u.nombre || u.email || u.id}</div>
                          <div className="text-[11px] text-zinc-500 truncate">{u.email}</div>
                        </div>
                        <span className="shrink-0 inline-flex items-center gap-1.5 text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase tracking-wide bg-white/[0.05] text-zinc-400 group-hover:bg-amber-500/20 group-hover:text-amber-200">
                          {rolLabel(u)}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ), document.body)}
    </>
  )
}
